import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import ExtensionErrorBoundary from './ExtensionErrorBoundary';
import * as styles from './ExtensionPost.module.scss';
import ExtensionTopicBar from './ExtensionTopicBar';
import ExtensionMagneticWall from './magneticWall/ExtensionMagneticWall';
import sendMessageToSlabFrame, {
  listenerFrameMessage,
} from './sendMessageToSlabFrame';
import Button from '../../components/Button';
import ErrorBoundary from '../../components/ErrorBoundary';
import ScrollArea from '../../components/ScrollArea';
import SVGIcon from '../../components/SVGIcon';
import Timestamp from '../../components/Timestamp';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillContext, { useQuill } from '../../editor/QuillContext';
import { gql } from '../../graphql/generated';
import resizeHandlerIcon from '../../icons/extension/resize-handler.svg';
import clearIcon from '../../icons/non-prefilled/16x16/clear.svg';
import collapseIcon from '../../icons/non-prefilled/16x16/collapse.svg';
import expandIcon from '../../icons/non-prefilled/16x16/expand.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import ControlBarContributorsButton from '../../post/controlBar/ControlBarContributorsButton';
import PostPreviewQuillComponent from '../../post/PostPreviewQuillComponent';
import postPath from '../../utils/url/postPath';

const POST_QUERY = gql(/** @complexity 26 */ `
  query ExtensionPost(
    $id: ID!
  ) {
    post(
      id: $id
    ) {
      id
      content {
        id
        delta
      }
      updatedAt
      externalPost {
        id
      }
      ...ControlBarContributorsButton_post
      ...ExtensionTopicBar_post
      ...ExtensionMagneticWall_post
      ...postPath_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface PostContentProps {
  id: string;
  persisted: boolean;
  expanded: boolean;
}

const PostContent = ({ id, persisted, expanded }: PostContentProps) => {
  const { data } = useQuery(POST_QUERY, { variables: { id } });

  const draggingStateRef = useRef<{
    capturedElement: Element;
    hasCaptured: boolean;
  } | null>(null);
  const handleStartDragging = (event: React.PointerEvent) => {
    if (!(event.target instanceof Element)) return;

    if (window.getComputedStyle(event.target).cursor !== 'move') return;

    draggingStateRef.current = {
      capturedElement: event.target,
      hasCaptured: false,
    };
    sendMessageToSlabFrame({
      'slab.type': 'dragging.start',
      'slab.data': [event.screenX, event.screenY],
    });
  };

  const handleEndDragging = (event: React.PointerEvent) => {
    if (!draggingStateRef.current) return;

    if (draggingStateRef.current.hasCaptured) {
      draggingStateRef.current.capturedElement.releasePointerCapture(
        event.pointerId,
      );
    }
    draggingStateRef.current = null;
    sendMessageToSlabFrame({ 'slab.type': 'dragging.end' });
  };

  const handleDragging = (event: React.PointerEvent) => {
    if (!draggingStateRef.current) return;

    if (!draggingStateRef.current.hasCaptured) {
      draggingStateRef.current.hasCaptured = true;
      draggingStateRef.current.capturedElement.setPointerCapture(
        event.pointerId,
      );
    }

    sendMessageToSlabFrame({
      'slab.type': 'dragging.move',
      'slab.data': [event.screenX, event.screenY],
    });
  };

  const resizingStateRef = useRef<{ capturedElement: Element } | null>(null);
  const handleStartResizing = (event: React.PointerEvent) => {
    if (!(event.target instanceof Element)) return;

    resizingStateRef.current = { capturedElement: event.target };
    event.target.setPointerCapture(event.pointerId);
    sendMessageToSlabFrame({
      'slab.type': 'resize.start',
      'slab.data': [event.screenX, event.screenY],
    });
  };

  const handleEndResizing = (event: React.PointerEvent) => {
    if (!resizingStateRef.current) return;

    resizingStateRef.current.capturedElement.releasePointerCapture(
      event.pointerId,
    );
    resizingStateRef.current = null;
    sendMessageToSlabFrame({ 'slab.type': 'resize.end' });
  };

  const handleResizing = (event: React.PointerEvent) => {
    if (!resizingStateRef.current) return;

    sendMessageToSlabFrame({
      'slab.type': 'resize.move',
      'slab.data': [event.screenX, event.screenY],
    });
  };

  const { quill } = useQuill();

  useEffect(() => {
    if (!expanded) return undefined;

    const listener = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();

      sendMessageToSlabFrame({
        'slab.type': 'expand',
        'slab.data': false,
      });
    };

    document.body.addEventListener('keydown', listener);

    return () => {
      document.body.removeEventListener('keydown', listener);
    };
  }, [expanded]);

  return data ? (
    <div className={styles.container}>
      <ScrollArea className={styles.scrollArea}>
        <div
          className={classNames(styles.contentContainer, {
            [styles.isMoveable]: !expanded && persisted,
          })}
          onPointerMove={handleDragging}
          onPointerDown={handleStartDragging}
          onPointerLeave={handleEndDragging}
          onPointerUp={handleEndDragging}
        >
          <div className={styles.headerControls}>
            <div className={styles.controlBarContainer}>
              <ControlBarContributorsButton
                post={data.post}
                interactionsDisabled
              >
                {data.post.externalPost ? 'Synced' : 'Edited'}{' '}
                <Timestamp date={data.post.updatedAt} />
              </ControlBarContributorsButton>
            </div>
            <div className={styles.topicBarContainer}>
              <ExtensionTopicBar post={data.post} />
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.editorContainer}>
              <AssetTokenProvider entityAssetToken={data.entityAssetToken}>
                <PostPreviewQuillComponent
                  namespace="extensionPostCard"
                  content={data.post.content.delta}
                  rootClassName={styles.editorRoot}
                />
              </AssetTokenProvider>
            </div>
            <div className={styles.magneticWallContainer}>
              {quill && (
                <ExtensionMagneticWall quill={quill} post={data.post} />
              )}
            </div>
          </div>
        </div>
      </ScrollArea>
      {!expanded && persisted && (
        <button
          type="button"
          className={styles.resizeHandler}
          onPointerDown={handleStartResizing}
          onPointerUp={handleEndResizing}
          onPointerLeave={handleEndResizing}
          onPointerMove={handleResizing}
        >
          <SVGIcon>{resizeHandlerIcon}</SVGIcon>
        </button>
      )}
      <div className={styles.buttonGroup}>
        <Button
          size="small"
          className={styles.button}
          onClick={() => {
            sendMessageToSlabFrame({
              'slab.type': 'close',
            });
          }}
        >
          <SVGIcon size="small">{clearIcon}</SVGIcon>
        </Button>
        <Button
          size="small"
          className={styles.button}
          onClick={() => {
            sendMessageToSlabFrame({
              'slab.type': 'expand',
              'slab.data': !expanded,
            });
          }}
        >
          <SVGIcon size="small">{expanded ? collapseIcon : expandIcon}</SVGIcon>
        </Button>
        <Button size="small" to={postPath(data.post)} className={styles.button}>
          <SVGIcon size="small">{newTabIcon}</SVGIcon>
        </Button>
      </div>
    </div>
  ) : null;
};

const ExtensionPost = () => {
  const [postParams, setPostParams] = useState<{
    id: string;
    isPersisted: boolean;
    isExpanded: boolean;
  } | null>(null);

  useEffect(() => {
    const cleanup = listenerFrameMessage((message) => {
      if (message['slab.type'] === 'post.load') {
        setPostParams(message['slab.data']);
      } else if (message['slab.type'] === 'post.unload') {
        setPostParams(null);
      }
    });

    sendMessageToSlabFrame({ 'slab.type': 'ready' });

    return cleanup;
  }, []);

  return (
    postParams && (
      <ErrorBoundary
        name="ExtensionPost"
        renderFallback={() => (
          <ExtensionErrorBoundary
            errorMessage={
              <>
                Unable to load post content.
                <br />
                Please refresh the page or try again later.
              </>
            }
          />
        )}
      >
        <QuillContext>
          <PostContent
            key={postParams.id}
            id={postParams.id}
            persisted={postParams.isPersisted}
            expanded={postParams.isExpanded}
          />
        </QuillContext>
      </ErrorBoundary>
    )
  );
};

export default ExtensionPost;
